import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, useAuth } from "../App";
import { toast } from "sonner";
import { isSuperAdmin } from "../hooks/useSuperAdmin";
import StaleUpdateModal from "../components/StaleUpdateModal";
import useShortcuts from "../hooks/useShortcuts";

import UserAutocomplete from "../components/UserAutocomplete";
import MentionTextarea from "../components/MentionTextarea";
import ShareButton from "../components/ShareButton";
import MobileSkeleton from "../components/MobileSkeleton";
import EditableField from "../components/ui/editable-field";
import { STATUS_OPTIONS, PRIORITY_OPTIONS } from "../lib/statusColors";

function SubtasksSection({ task, taskId, onUpdate }) {
  const [newTitle, setNewTitle] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const subtasks = (task?.subtasks || []).filter(st => !st.is_review); // Hide legacy auto-review subtasks

  const addSubtask = async () => {
    if (!newTitle.trim()) return;
    try {
      await api.post(`/tasks/${taskId}/subtasks`, { title: newTitle.trim() });
      setNewTitle("");
      setAdding(false);
      onUpdate();
      toast.success("Subtask added");
    } catch (e) { toast.error("Failed to add subtask"); }
  };

  const toggleSubtask = async (subtaskId, currentDone) => {
    try {
      await api.put(`/tasks/${taskId}/subtasks/${subtaskId}`, { done: !currentDone });
      onUpdate();
    } catch (e) { toast.error("Failed to update subtask"); }
  };

  const saveEdit = async (subtaskId) => {
    if (!editTitle.trim()) return;
    try {
      await api.put(`/tasks/${taskId}/subtasks/${subtaskId}`, { title: editTitle.trim() });
      setEditingId(null);
      setEditTitle("");
      onUpdate();
    } catch (e) { toast.error("Failed to update subtask"); }
  };

  const deleteSubtask = async (subtaskId) => {
    try {
      await api.delete(`/tasks/${taskId}/subtasks/${subtaskId}`);
      onUpdate();
      toast.success("Subtask removed");
    } catch (e) { toast.error("Failed to delete subtask"); }
  };

  const doneCount = subtasks.filter(s => s.done).length;
  const progress = subtasks.length > 0 ? Math.round((doneCount / subtasks.length) * 100) : 0;

  return (
    <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-medium text-white">Subtasks</h3>
          {subtasks.length > 0 && (
            <span className="text-xs text-[var(--t2)]">{doneCount}/{subtasks.length}</span>
          )}
        </div>
        <button onClick={() => setAdding(!adding)}
          className="px-3 py-1.5 rounded-lg text-xs bg-[var(--s2)] border border-[var(--bdrh)] text-white hover:bg-[var(--s3)]">
          {adding ? "Cancel" : "+ Add Subtask"}
        </button>
      </div>

      {/* Progress bar */}
      {subtasks.length > 0 && (
        <div className="w-full h-1.5 bg-[var(--s2)] rounded-full mb-3 overflow-hidden">
          <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}

      {/* Add form */}
      {adding && (
        <div className="flex gap-2 mb-3">
          <input
            className="flex-1 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg p-2 text-white text-sm focus:border-[var(--bdrh)] focus:outline-none"
            placeholder="Subtask title..."
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addSubtask()}
            autoFocus
          />
          <button onClick={addSubtask} className="px-3 py-2 bg-[var(--r)] text-white rounded-lg text-sm font-medium hover:opacity-90 whitespace-nowrap">Add</button>
        </div>
      )}

      {/* Subtask list */}
      {subtasks.length === 0 && !adding && <p className="text-[var(--t3)] text-sm">No subtasks yet</p>}
      <div className="space-y-1">
        {subtasks.map(st => (
          <div key={st.subtask_id} className="flex items-center gap-2.5 px-2 py-2.5 rounded-lg hover:bg-[var(--s2)] group">
            <button onClick={() => toggleSubtask(st.subtask_id, st.done)}
              className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors cursor-pointer ${st.done ? "bg-green-500/20 border-green-500/50" : "border-[var(--bdrh)] hover:border-green-400 hover:bg-green-400/10"}`}>
              {st.done && <span className="text-green-400 text-xs">✓</span>}
            </button>
            <div className="flex-1 min-w-0">
              {editingId === st.subtask_id ? (
                <div className="flex gap-2">
                  <input className="flex-1 bg-[var(--s1)] border border-[var(--bdrh)] rounded px-2 py-1 text-sm text-[var(--t1)] focus:outline-none focus:border-[#555]"
                    value={editTitle} onChange={e => setEditTitle(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") saveEdit(st.subtask_id); if (e.key === "Escape") setEditingId(null); }}
                    autoFocus />
                  <button onClick={() => saveEdit(st.subtask_id)} className="text-xs text-green-400 hover:text-green-300">Save</button>
                  <button onClick={() => setEditingId(null)} className="text-xs text-[#666] hover:text-[var(--t1)]">Cancel</button>
                </div>
              ) : (
                <span className={`text-sm ${st.done ? "text-[var(--t3)] line-through" : "text-white/80"}`}>{st.title}</span>
              )}
              {st.assignee_name && editingId !== st.subtask_id && <span className="text-xs text-[var(--t3)] ml-2">{st.assignee_name}</span>}
              {st.updated_at && editingId !== st.subtask_id && <span className="text-xs text-white/20 ml-2">· {new Date(st.updated_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>}
            </div>
            {editingId !== st.subtask_id && (
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => { setEditingId(st.subtask_id); setEditTitle(st.title); }}
                  className="text-xs px-2 py-1 rounded text-[#666] hover:text-[var(--t1)] hover:bg-[var(--s2)] transition-colors">Edit</button>
                <button onClick={() => deleteSubtask(st.subtask_id)}
                  className="text-xs px-2 py-1 rounded text-[#666] hover:text-red-400 hover:bg-red-500/10 transition-colors">×</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TaskDetailPage() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [comment, setComment] = useState("");
  const [showStaleModal, setShowStaleModal] = useState(false);
  const [leads, setLeads] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkName, setLinkName] = useState("");
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [showDeleteTask, setShowDeleteTask] = useState(false);


  const fetchAttachments = async () => {
    try {
      const res = await api.get(`/tasks/${taskId}/attachments`);
      setAttachments(res.data || []);
    } catch (e) { console.error("Failed to fetch attachments", e); }
  };

useEffect(() => {
    fetchTask();
    fetchAttachments();
    fetchLeads();
    fetchBriefs();
  }, [taskId]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Cmd/Ctrl+E toggles advanced edit. Must be at top level (rules-of-hooks).
  useShortcuts(
    [
      { keys: "mod+e", description: "Edit task (advanced)", group: "Task",
        handler: () => setEditing((v) => !v) },
      { keys: "escape", description: "Cancel edit",
        handler: () => setEditing((v) => v ? false : v) },
    ],
    { enabled: !!task && !showStaleModal }
  );

  const fetchTask = async () => {
    try {
      const res = await api.get(`/tasks/${taskId}`);
      setTask(res.data);
      if (res.data.stale && !showStaleModal) setShowStaleModal(true);
      setForm({
        title: res.data.title || "",
        description: res.data.description || "",
        assignee_id: res.data.assignee_id || "",
        reviewer_id: res.data.reviewer_id || "",
        priority: res.data.priority || "medium",
        status: res.data.status || "pending",
        start_date: res.data.start_date ? res.data.start_date.split("T")[0] : "",
        due_date: res.data.due_date ? res.data.due_date.split("T")[0] : "",
        due_time: res.data.due_time || "",
        project_id: res.data.project_id || "",
        lead_id: res.data.lead_id || "",
        brief_id: res.data.brief_id || "",
      });
    } catch (e) {
      toast.error("Failed to load task");
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async () => {
    try {
      const res = await api.get("/leads");
      setLeads(res.data || []);
    } catch(e) {}
  };

  const fetchBriefs = async () => {
    try {
      const res = await api.get("/bizdev/briefs");
      setBriefs(res.data || []);
    } catch(e) {}
  };

  const saveTask = async () => {
    if (!form.assignee_id) { toast.error("Assignee is required"); return; }
    if (!form.reviewer_id) { toast.error("Reviewer is required"); return; }
    if (!form.due_date) { toast.error("Due Date is required"); return; }
    try {
      const res = await api.put(`/tasks/${taskId}`, form);
      setTask(res.data);
      setEditing(false);
      toast.success("Task updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  // Patch a single field inline (used by <EditableField> — optimistic, no modal)
  const patchTask = async (patch) => {
    // Optimistic: update local state immediately
    const prev = task;
    setTask((t) => ({ ...(t || {}), ...patch }));
    try {
      const res = await api.put(`/tasks/${taskId}`, patch);
      setTask(res.data);
      if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(8);
    } catch (e) {
      // Rollback on failure and surface the server error (EditableField also renders the thrown error inline)
      setTask(prev);
      throw e;
    }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    try {
      const res = await api.post(`/tasks/${taskId}/comments`, { text: comment });
      setTask(res.data);
      setComment("");
      toast.success("Comment added");
    } catch (e) {
      toast.error("Failed to add comment");
    }
  };

  const toggleComplete = async () => {
    // Use the canonical status values from STATUS_OPTIONS ("Open"/"Done") —
    // sending lowercase "completed"/"pending" used to leave tasks in a state
    // that other pages (overdue list, kanban, dashboards) didn't recognise,
    // which is why "completed" tasks kept popping back up as overdue.
    const isDone = task.status === "Done" || task.status === "Completed" || task.status === "completed";
    const newStatus = isDone ? "Open" : "Done";
    try {
      const res = await api.put(`/tasks/${taskId}`, { status: newStatus });
      setTask(res.data);
      toast.success(newStatus === "Done" ? "Marked complete" : "Reopened");
    } catch(e) {
      toast.error("Failed");
    }
  };

  if (loading) return window.innerWidth < 768 ? <MobileSkeleton type="detail" /> : <div className="flex items-center justify-center h-64"><div className="animate-pulse text-[var(--t3)] text-xl font-heading">LOADING...</div></div>;
  if (!task) return null;

  // Stale update modal
  if (showStaleModal) return <StaleUpdateModal entityType="task" entityId={taskId} entityTitle={task.title} onSave={() => { setShowStaleModal(false); fetchTask(); }} />;

  const uid = user?.user_id || user?.id;
  const labelStyle = "text-xs text-[var(--t2)] mb-1";
  const inputStyle = "w-full bg-[var(--s1)] border border-[var(--bdr)] rounded-lg p-2 text-white text-sm focus:border-[var(--bdrh)] focus:outline-none";
  const sectionStyle = "mb-3";

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      await api.post(`/tasks/${taskId}/attachments/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded");
      fetchAttachments();
    } catch (err) { toast.error("Upload failed"); }
  };

  const handleAddLink = async () => {
    if (!linkUrl.trim()) return;
    try {
      await api.post(`/tasks/${taskId}/attachments/link`, { url: linkUrl, name: linkName || linkUrl });
      toast.success("Link added");
      setLinkUrl(""); setLinkName(""); setShowLinkForm(false);
      fetchAttachments();
    } catch (err) { toast.error("Failed to add link"); }
  };

  // handleDeleteAttachment replaced by undoDeleteAttachment below — keep signature if referenced elsewhere
  // eslint-disable-next-line no-unused-vars
  const handleDeleteAttachment = async (attId) => {
    try {
      await api.delete(`/tasks/${taskId}/attachments/${attId}`);
      fetchAttachments();
    } catch (err) { toast.error("Delete failed"); }
  };



  const isAdmin = (user?.role || "").toLowerCase() === "admin" || isSuperAdmin(user?.email);

  // Delete with 5s undo window — no confirm modal. Navigates back immediately;
  // if user clicks Undo the delete never happens. Otherwise server DELETE fires on auto-close.
  const handleDeleteTask = () => {
    let undone = false;
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(15);
    // Leave the page first — feels instant
    navigate("/tasks");
    toast(`Deleted: ${(task?.title || "task").slice(0, 40)}`, {
      duration: 5000,
      action: {
        label: "Undo",
        onClick: () => {
          undone = true;
          // Bring them back to the task page
          navigate(`/tasks/${taskId}`);
        },
      },
      onAutoClose: () => {
        if (undone) return;
        setTimeout(async () => {
          try { await api.delete(`/tasks/${taskId}`); }
          catch (e) {
            console.error("Delete failed", e);
            toast.error("Delete failed — task still exists");
          }
        }, 50);
      },
      onDismiss: () => {
        if (undone) return;
        setTimeout(async () => {
          try { await api.delete(`/tasks/${taskId}`); }
          catch (e) {
            console.error("Delete failed", e);
            toast.error("Delete failed — task still exists");
          }
        }, 50);
      },
    });
  };

  // Attachment delete with undo (replaces window.confirm)
  const undoDeleteAttachment = (attachment) => {
    const attId = attachment.attachment_id;
    const snapshot = [...attachments];
    // Optimistic remove
    setAttachments((prev) => prev.filter((a) => a.attachment_id !== attId));
    let undone = false;
    toast(`Removed: ${(attachment.name || attachment.url || "attachment").slice(0, 40)}`, {
      duration: 5000,
      action: { label: "Undo", onClick: () => { undone = true; setAttachments(snapshot); } },
      onAutoClose: () => {
        if (undone) return;
        setTimeout(async () => {
          try { await api.delete(`/tasks/${taskId}/attachments/${attId}`); }
          catch (e) { setAttachments(snapshot); toast.error("Delete failed — restored"); }
        }, 50);
      },
      onDismiss: () => {
        if (undone) return;
        setTimeout(async () => {
          try { await api.delete(`/tasks/${taskId}/attachments/${attId}`); }
          catch (e) { setAttachments(snapshot); toast.error("Delete failed — restored"); }
        }, 50);
      },
    });
  };


  return (
    <div className="dm-page-container max-w-3xl mx-auto space-y-5" data-testid="task-detail">
      <div className="dm-detail-header flex items-center gap-3">
        <button onClick={() => navigate(-1)} style={{ padding: 8, color: "var(--t3)", cursor: "pointer", background: "none", border: "none", fontSize: 20 }}>&larr;</button>
        <div className="flex-1 min-w-0">
          {/* Title is inline-editable — click to edit, Enter to save, Esc to cancel */}
          <EditableField
            value={task.title}
            onSave={(v) => patchTask({ title: v })}
            placeholder="Untitled task"
            size="lg"
            className="text-xl font-heading text-[var(--t1)]"
            inputClassName="text-xl font-heading"
          />
          <span className={`text-xs px-2 py-0.5 rounded-full inline-block mt-1 ${task.status === "completed" ? "bg-green-500/20 text-green-400" : task.status === "in_progress" ? "bg-blue-500/20 text-blue-400" : "bg-yellow-500/20 text-yellow-400"}`}>{task.status}</span>
        </div>
        <div className="dm-detail-actions flex items-center gap-2">
        <button onClick={toggleComplete} style={{ padding: "6px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, background: "var(--g)", color: "#fff", border: "none", cursor: "pointer" }}>
          {task.status === "completed" ? "Reopen" : "Complete"}
        </button>
        <button onClick={() => setEditing(!editing)} title="Advanced edit (Cmd+E)" style={{ padding: "6px 14px", borderRadius: 8, fontSize: 13, fontWeight: 500, background: "var(--s3)", color: "var(--t1)", border: "1px solid var(--bdr)", cursor: "pointer" }}>
          {editing ? "\u2715 Cancel" : "Edit"}
        </button>
        {isAdmin && <button onClick={handleDeleteTask} style={{ padding: "6px 14px", borderRadius: 8, fontSize: 13, fontWeight: 500, background: "rgba(239,68,68,.1)", color: "#EF4444", border: "1px solid rgba(239,68,68,.2)", cursor: "pointer" }}>Delete</button>}
        <ShareButton title={task?.title} text={`Task: ${task?.title}`} />
        </div>
      </div>

      {editing ? (
        <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-xl p-5 space-y-4">
          <div className={sectionStyle}>
            <label className={labelStyle}>Title</label>
            <input className={inputStyle} value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
          </div>
          <div className={sectionStyle}>
            <label className={labelStyle}>Description</label>
            <textarea className={inputStyle} rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className={sectionStyle}>
              <label className={labelStyle}>Assign To *</label>
              <UserAutocomplete value={form.assignee_id} onChange={val => setForm({...form, assignee_id: val})} />
            </div>
            <div className={sectionStyle}>
              <label className={labelStyle}>Reviewer *</label>
              <UserAutocomplete value={form.reviewer_id} onChange={val => setForm({...form, reviewer_id: val})} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className={sectionStyle}>
              <label className={labelStyle}>Priority</label>
              <select className={inputStyle} value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className={sectionStyle}>
              <label className={labelStyle}>Status</label>
              <select className={inputStyle} value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                <option value="Open">Open</option>
                <option value="In Progress">In Progress</option>
                <option value="Review">Review</option>
                <option value="Paused">Paused</option>
                <option value="Done">Done</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className={sectionStyle}>
              <label className={labelStyle}>Project</label>
              <input className={inputStyle} value={form.project_id} onChange={e => setForm({...form, project_id: e.target.value})} placeholder="Project ID" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className={sectionStyle}>
              <label className={labelStyle}>Start Date</label>
              <input type="date" className={inputStyle} value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} />
            </div>
            <div className={sectionStyle}>
              <label className={labelStyle}>Due Date *</label>
              <input type="date" className={inputStyle} value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} required />
            </div>
            <div className={sectionStyle}>
              <label className={labelStyle}>Due Time</label>
              <select className={inputStyle} value={form.due_time} onChange={e => setForm({...form, due_time: e.target.value})}>
                <option value="">No time</option>
                {["09:00","09:30","10:00","10:30","11:00","11:30","12:00","12:30","13:00","13:30","14:00","14:30","15:00","15:30","16:00","16:30","17:00","17:30","18:00","18:30","19:00","19:30","20:00"].map(t => {
                  const [h,m] = t.split(":"); const hr = parseInt(h); const ampm = hr >= 12 ? "PM" : "AM"; const hr12 = hr > 12 ? hr-12 : hr === 0 ? 12 : hr;
                  return <option key={t} value={t}>{`${hr12}:${m} ${ampm}`}</option>;
                })}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className={sectionStyle}>
              <label className={labelStyle}>Lead / Account</label>
              <select className={inputStyle} value={form.lead_id} onChange={e => setForm({...form, lead_id: e.target.value})}>
                <option value="">-- None --</option>
                {leads.map(l => <option key={l.lead_id || l._id} value={l.lead_id || l._id}>{l.company_name || l.lead_id}</option>)}
              </select>
            </div>
            <div className={sectionStyle}>
              <label className={labelStyle}>Brief</label>
              <select className={inputStyle} value={form.brief_id} onChange={e => setForm({...form, brief_id: e.target.value})}>
                <option value="">-- None --</option>
                {briefs.map(b => <option key={b.brief_id || b._id} value={b.brief_id || b._id}>{b.title || b.brief_id}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={saveTask} style={{ padding: "8px 20px", borderRadius: 8, fontSize: 13, fontWeight: 700, background: "var(--r)", color: "#fff", border: "none", cursor: "pointer" }}>Save Changes</button>
            <button onClick={() => setEditing(false)} style={{ padding: "8px 20px", borderRadius: 8, fontSize: 13, fontWeight: 500, background: "var(--s3)", color: "var(--t1)", border: "1px solid var(--bdr)", cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-xl p-5 space-y-4">
          {/* Description — inline editable textarea. Empty state shows "Add description…" */}
          <div>
            <div className="text-xs text-[var(--t3)] mb-1">Description</div>
            <EditableField
              type="textarea"
              value={task.description || ""}
              onSave={(v) => patchTask({ description: v })}
              placeholder="Add description…"
              className="text-[var(--t2)] text-sm"
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-3 text-sm">
            {/* Read-only fields — changed via the Edit button (advanced form) */}
            <div>
              <div className="text-xs text-[var(--t3)] mb-1">Created by</div>
              <div className="text-white/80">{task.created_by_name || task.assigned_by_name || "—"}</div>
            </div>
            <div>
              <div className="text-xs text-[var(--t3)] mb-1">Assigned to</div>
              <div className="text-white/80">{task.assignee_name || task.assignee_id || "Unassigned"}</div>
            </div>
            <div>
              <div className="text-xs text-[var(--t3)] mb-1">Reviewer</div>
              <div className="text-white/80">{task.reviewer_name || task.reviewer_id || "No reviewer"}</div>
            </div>

            {/* Priority — inline select */}
            <div>
              <EditableField
                label="Priority"
                type="select"
                value={task.priority || ""}
                options={[
                  { value: "", label: "—" },
                  ...PRIORITY_OPTIONS.map(p => ({ value: p.toLowerCase(), label: p })),
                ]}
                onSave={(v) => patchTask({ priority: v })}
                displayFormatter={(v) => v ? v.charAt(0).toUpperCase() + v.slice(1) : "—"}
                className="text-white/80 capitalize"
              />
            </div>

            {/* Status — inline select using STATUS_OPTIONS */}
            <div>
              <EditableField
                label="Status"
                type="select"
                value={task.status || ""}
                options={STATUS_OPTIONS.map(s => ({ value: s, label: s }))}
                onSave={(v) => patchTask({ status: v })}
                className="text-white/80"
              />
            </div>

            {/* Start date — inline date picker */}
            <div>
              <EditableField
                label="Start"
                type="date"
                value={task.start_date ? task.start_date.split("T")[0] : ""}
                onSave={(v) => patchTask({ start_date: v || null })}
                displayFormatter={(v) => v ? new Date(v + "T00:00:00").toLocaleDateString() : "—"}
                className="text-white/80"
                emptyText="Set start"
              />
            </div>

            {/* Due date — inline date picker */}
            <div>
              <EditableField
                label="Due"
                type="date"
                value={task.due_date ? task.due_date.split("T")[0] : ""}
                onSave={(v) => patchTask({ due_date: v || null })}
                displayFormatter={(v) => v ? new Date(v + "T00:00:00").toLocaleDateString() : "No date"}
                className="text-white/80"
                emptyText="Set due date"
              />
            </div>

            {/* Due time — inline select */}
            <div>
              <EditableField
                label="Due time"
                type="select"
                value={task.due_time || ""}
                options={[
                  { value: "", label: "No time" },
                  ...["09:00","09:30","10:00","10:30","11:00","11:30","12:00","12:30","13:00","13:30","14:00","14:30","15:00","15:30","16:00","16:30","17:00","17:30","18:00","18:30","19:00","19:30","20:00"].map(t => {
                    const [h,m] = t.split(":"); const hr = parseInt(h); const ampm = hr >= 12 ? "PM" : "AM"; const hr12 = hr > 12 ? hr-12 : hr === 0 ? 12 : hr;
                    return { value: t, label: `${hr12}:${m} ${ampm}` };
                  }),
                ]}
                onSave={(v) => patchTask({ due_time: v || null })}
                displayFormatter={(v) => {
                  if (!v) return "—";
                  const [h,m] = v.split(":"); const hr = parseInt(h); const ampm = hr >= 12 ? "PM" : "AM"; const hr12 = hr > 12 ? hr-12 : hr === 0 ? 12 : hr;
                  return `${hr12}:${m} ${ampm}`;
                }}
                className="text-white/80"
                emptyText="Set time"
              />
            </div>

            <div>
              <div className="text-xs text-[var(--t3)] mb-1">Project</div>
              <div className="text-white/80">{task.project_name || task.project || "—"}</div>
            </div>
            <div>
              <div className="text-xs text-[var(--t3)] mb-1">Lead / Account</div>
              <div className="text-white/80">{task.lead_name || task.lead_id || "—"}</div>
            </div>
            <div>
              <div className="text-xs text-[var(--t3)] mb-1">Brief</div>
              <div className="text-white/80">{task.brief_title || task.brief_id || "—"}</div>
            </div>
          </div>
          <p className="text-[10px] text-[var(--t3)] opacity-60 pt-1">Click any field to edit · Cmd+E for advanced edit (people &amp; links)</p>
        </div>
      )}

      {/* Subtasks */}
      <SubtasksSection task={task} taskId={taskId} onUpdate={fetchTask} />

      {/* Comments */}
      <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-xl p-5">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="text-sm font-medium text-white">Comments</h3>
          <span className="text-[10px] text-[var(--t3)] opacity-70">Enter to post · Shift+Enter for newline</span>
        </div>
        <div className="mb-4">
          <MentionTextarea value={comment} onChange={setComment} onSubmit={addComment} placeholder="Add a comment... (type @ to mention)" rows={1} />
        </div>
        {(task.comments || []).length === 0 && <p className="text-[var(--t3)] text-sm">No comments yet</p>}
        {(task.comments || []).map(c => (
          <div key={c.comment_id} className="p-3 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg mb-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-white/80 text-xs font-medium">{c.author_name}</span>
              <span className="text-[#444] text-xs">{new Date(c.created_at).toLocaleDateString()}</span>
            </div>
            <p className="text-[var(--t2)] text-sm">{c.text}</p>
          </div>
        ))}
      </div>
      {/* Attachments */}
      <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-white">Attachments</h3>
          <div className="flex gap-2">
            <button onClick={() => setShowLinkForm(!showLinkForm)} className="px-3 py-1.5 rounded-lg text-xs bg-[var(--s2)] border border-[var(--bdrh)] text-white hover:bg-[var(--s3)]">
              {showLinkForm ? "Cancel" : "+ Add Link"}
            </button>
            <label className="px-3 py-1.5 rounded-lg text-xs bg-[var(--s2)] border border-[var(--bdrh)] text-white hover:bg-[var(--s3)] cursor-pointer">
              Upload File
              <input type="file" className="hidden" onChange={handleFileUpload} />
            </label>
          </div>
        </div>
        {showLinkForm && (
          <div className="flex gap-2 mb-3">
            <input className={inputStyle} placeholder="URL" value={linkUrl} onChange={e => setLinkUrl(e.target.value)} />
            <input className={inputStyle + " max-w-[200px]"} placeholder="Name (optional)" value={linkName} onChange={e => setLinkName(e.target.value)} />
            <button onClick={handleAddLink} className="px-3 py-2 bg-[var(--r)] text-white rounded-lg text-sm font-medium hover:opacity-90 whitespace-nowrap">Add</button>
          </div>
        )}
        {attachments.length === 0 && <p className="text-[var(--t3)] text-sm">No attachments</p>}
        {attachments.map(att => (
          <div key={att.attachment_id} className="flex items-center justify-between p-2.5 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[var(--t3)] text-xs">{att.type === "link" ? "🔗" : "📎"}</span>
              {att.type === "link" ? (
                <a href={att.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-sm truncate">{att.name || att.url}</a>
              ) : (
                <a href={att.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-sm truncate">{att.name || "File"}</a>
              )}
            </div>
            <button onClick={() => undoDeleteAttachment(att)} className="text-red-400/60 hover:text-red-400 text-xs ml-2 shrink-0">Delete</button>
          </div>
        ))}
      </div>

    </div>
  );
}
